import React from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';

export const BatchComparison = ({ batchData, roleIndex }) => {
  const bd = batchData;
  if (!bd || !bd.batches || bd.batches.length < 2) return null;
  const sh = (bd.source_history || []).slice(-1)[0];

  return (
    <div className="mb-4 p-4 bg-white border border-indigo-200 rounded-lg" data-testid={`batch-comparison-${roleIndex}`}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-bold text-[#0A1F44]">Re-Source Comparison</span>
        {sh?.improved && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-bold rounded">IMPROVED</span>}
        {sh && !sh.improved && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[8px] font-bold rounded">NO IMPROVEMENT</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        {bd.batches.map((b, bi) => (
          <div key={b.batch} className={`p-3 rounded-lg border ${bi === bd.batches.length - 1 ? 'bg-indigo-50/50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-[#0A1F44] uppercase">{b.batch === 'v1' ? 'Original' : `Re-Sourced ${b.batch}`}</span>
              <span className="text-[10px] text-gray-400">{b.total} candidates</span>
            </div>
            <div className="flex items-center gap-3">
              <div>
                <p className={`text-lg font-bold ${b.avg_score >= 40 ? 'text-emerald-600' : b.avg_score >= 25 ? 'text-amber-600' : 'text-red-500'}`}>{b.avg_score}</p>
                <p className="text-[8px] text-gray-400 uppercase">Avg Score</p>
              </div>
              <div className="flex gap-2 text-[9px]">
                <span className="text-emerald-600 font-semibold">H:{b.high_fit}</span>
                <span className="text-amber-600 font-semibold">M:{b.medium_fit}</span>
                <span className="text-red-500 font-semibold">L:{b.low_fit}</span>
              </div>
            </div>
            <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden flex">
              {b.high_fit > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${(b.high_fit / Math.max(b.total, 1)) * 100}%` }}></div>}
              {b.medium_fit > 0 && <div className="bg-amber-400 h-full" style={{ width: `${(b.medium_fit / Math.max(b.total, 1)) * 100}%` }}></div>}
              {b.low_fit > 0 && <div className="bg-red-400 h-full" style={{ width: `${(b.low_fit / Math.max(b.total, 1)) * 100}%` }}></div>}
            </div>
          </div>
        ))}
      </div>

      {sh?.changes && sh.changes.length > 0 && (
        <div className="border-t border-gray-200 pt-2">
          <p className="text-[9px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">What Changed</p>
          {sh.changes.map((chg, ci) => (
            <div key={ci} className="mb-2 last:mb-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-semibold text-[#0A1F44] capitalize">{chg.field.replace('_', ' ')}</span>
                <span className="text-[8px] text-gray-400">{chg.reason}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[8px] text-gray-400 mb-0.5">Original</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(chg.original) ? chg.original : [chg.original]).map((v, vi) => (
                      <span key={vi} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[8px] rounded line-through">{String(v)}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[8px] text-gray-400 mb-0.5">Refined</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(chg.refined) ? chg.refined : [chg.refined]).map((v, vi) => (
                      <span key={vi} className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[8px] rounded font-medium">{String(v)}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sh && (
        <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-4">
          <div className="flex items-center gap-1">
            <TrendingUp className={`h-3 w-3 ${sh.improved ? 'text-emerald-500' : 'text-red-400'}`} />
            <span className="text-[10px] font-semibold text-[#0A1F44]">
              Avg Score: {sh.before?.avg} → {sh.after?.avg}
              <span className={`ml-1 ${sh.improved ? 'text-emerald-600' : 'text-red-500'}`}>
                ({sh.after?.avg - sh.before?.avg > 0 ? '+' : ''}{(sh.after?.avg - sh.before?.avg).toFixed(1)})
              </span>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500">
              High Fit: {sh.before?.high_fit} → {sh.after?.high_fit}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500">
              Medium: {sh.before?.medium} → {sh.after?.medium}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
